import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { runConnector, runConnectorDebug } from '../utils/api'

// ---------------------------------------------------------------------------
// Detect cfg.get("key") patterns from __init__ body
// ---------------------------------------------------------------------------
function detectConfigParams(code) {
  const params = new Set()
  // Match cfg.get("key") or cfg.get('key') with optional spaces
  const re = /cfg\.get\(\s*["']([^"']+)["']\s*\)/g
  let m
  while ((m = re.exec(code)) !== null) params.add(m[1])
  return [...params]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isSensitiveKey(key) {
  return /password|secret|token|key|pwd/i.test(key)
}

function tryFormat(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}

function StatusBadge({ status }) {
  const ok = status === 'success'
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${ok ? 'bg-[#1a3a1a] text-[#4ec994]' : 'bg-[#3a1a1a] text-[#f44747]'}`}>
      {ok
        ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      }
      {status}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Result viewer with syntax-like coloring for JSON
// ---------------------------------------------------------------------------
function ResultViewer({ result, runMs }) {
  const text = JSON.stringify(result, null, 2)
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-[#858585] uppercase tracking-widest">Output</span>
          {runMs !== null && (
            <span className="text-[10px] text-[#555]">{runMs}ms</span>
          )}
        </div>
        <StatusBadge status={result.status} />
      </div>
      <pre className="bg-[#1a1a1a] border border-[#3c3c3c] rounded-lg p-4 text-[11px] font-mono text-[#cccccc] overflow-auto max-h-64 whitespace-pre-wrap leading-relaxed">
        {text}
      </pre>
      {result.traceback && (
        <details className="mt-2">
          <summary className="text-[11px] text-[#f44747] cursor-pointer hover:opacity-80">Traceback</summary>
          <pre className="mt-1 bg-[#1a1a1a] border border-[#f4474730] rounded p-3 text-[10px] font-mono text-[#f44747] overflow-auto max-h-48 whitespace-pre-wrap leading-relaxed">
            {result.traceback}
          </pre>
        </details>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Debug trace viewer
// ---------------------------------------------------------------------------
function DebugViewer({ trace, truncated, runMs, filename }) {
  const [expanded, setExpanded] = useState(new Set())
  const toggle = (i) => setExpanded((prev) => {
    const next = new Set(prev)
    next.has(i) ? next.delete(i) : next.add(i)
    return next
  })

  const handleDownload = () => {
    const ts = new Date().toISOString().replace(/[:.]/g, '-')
    const base = filename ? filename.replace('.py', '') : 'connector'
    const name = `debug_${base}_${ts}.json`
    const content = JSON.stringify({ filename, runMs, truncated, trace }, null, 2)
    const blob = new Blob([content], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  }

  const renderVars = (vars) =>
    Object.entries(vars).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
      <span key={k} className="flex items-start gap-1.5 flex-wrap">
        <span style={{ color: '#9cdcfe' }}>{k}</span>
        <span style={{ color: '#555' }}>=</span>
        <span style={{ color: '#ce9178' }}>{JSON.stringify(v)}</span>
      </span>
    ))

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[10px] font-semibold text-[#858585] uppercase tracking-widest">Execution Flow</span>
        <span className="text-[10px] text-[#555]">{trace.length} eventos{runMs !== null ? ` · ${runMs}ms` : ''}</span>
        {truncated && (
          <span className="text-[10px] text-[#e5b84a] flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            truncado em 200
          </span>
        )}
        <button
          onClick={handleDownload}
          disabled={trace.length === 0}
          className="ml-auto flex items-center gap-1.5 text-[11px] text-[#555] hover:text-[#cccccc] px-2 py-0.5 border border-[#3c3c3c] hover:border-[#505050] rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
          title="Download trace as JSON"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          .json
        </button>
      </div>

      <div className="bg-[#141414] border border-[#3c3c3c] rounded-lg overflow-auto max-h-80 font-mono text-[11px]">
        {trace.length === 0 && (
          <div className="px-4 py-6 text-center text-[#444]">Nenhum evento capturado</div>
        )}
        {trace.map((evt, i) => {
          const hasDetails = evt.event === 'call'
            ? (evt.args && Object.keys(evt.args).filter(k => !k.startsWith('_')).length > 0)
            : evt.event === 'return'
            ? (evt.return_value !== undefined)
            : evt.event === 'line'
            ? (evt.changed && Object.keys(evt.changed).length > 0)
            : false
          const isOpen = expanded.has(i)

          return (
            <div key={i} className="border-b border-[#1c1c1c] last:border-0">
              <div
                className={`flex items-center gap-0 px-3 py-1.5 ${hasDetails ? 'cursor-pointer hover:bg-[#ffffff05]' : ''}`}
                onClick={hasDetails ? () => toggle(i) : undefined}
              >
                {/* line number */}
                <span className="w-8 flex-shrink-0 text-right text-[#333] mr-3 select-none">{evt.line_no}</span>

                {evt.event === 'call' && (
                  <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span style={{ color: '#6cb6f5' }} className="flex-shrink-0">→</span>
                    <span style={{ color: '#dcdcaa' }}>{evt.func}</span>
                    <span style={{ color: '#555' }}>()</span>
                    {evt.args && Object.keys(evt.args).filter(k => !k.startsWith('_')).length > 0 && (
                      <span className="text-[#3c3c3c] text-[9px] ml-1 flex-shrink-0">{isOpen ? '▾' : '▸'}</span>
                    )}
                  </span>
                )}

                {evt.event === 'return' && (
                  <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span style={{ color: '#4ec994' }} className="flex-shrink-0">←</span>
                    <span style={{ color: '#858585' }}>{evt.func}</span>
                    <span style={{ color: '#555' }}>returned</span>
                    <span style={{ color: '#ce9178' }} className="truncate">{JSON.stringify(evt.return_value)}</span>
                  </span>
                )}

                {evt.event === 'exception' && (
                  <span className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span style={{ color: '#f44747' }} className="flex-shrink-0">⚠</span>
                    <span style={{ color: '#f44747' }} className="truncate">{evt.exception}</span>
                  </span>
                )}

                {evt.event === 'line' && (
                  <span className="flex items-center gap-2 flex-1 min-w-0">
                    <span style={{ color: '#cccccc' }} className="flex-1 truncate">{evt.line.trim()}</span>
                    {evt.changed && Object.keys(evt.changed).length > 0 && (
                      <span className="text-[#3c3c3c] text-[9px] flex-shrink-0">{isOpen ? '▾' : '▸'}</span>
                    )}
                  </span>
                )}
              </div>

              {/* expanded details */}
              {isOpen && hasDetails && (
                <div className="pb-2 space-y-0.5 flex flex-col" style={{ paddingLeft: '56px' }}>
                  {evt.event === 'call' && evt.args &&
                    Object.entries(evt.args).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
                      <span key={k} className="flex items-start gap-1.5">
                        <span style={{ color: '#9cdcfe' }}>{k}</span>
                        <span style={{ color: '#555' }}>=</span>
                        <span style={{ color: '#ce9178' }}>{JSON.stringify(v)}</span>
                      </span>
                    ))
                  }
                  {evt.event === 'line' && evt.changed && renderVars(evt.changed)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------
export default function RunModal({ filename, code, onClose }) {
  const detectedParams = useMemo(() => detectConfigParams(code), [code])

  const [configValues, setConfigValues] = useState(() =>
    Object.fromEntries(detectedParams.map((k) => [k, '']))
  )
  const [payloadText, setPayloadText]   = useState('{\n  "data": {}\n}')
  const [payloadError, setPayloadError] = useState('')
  const [isRunning, setIsRunning]         = useState(false)
  const [isDebugRunning, setIsDebugRunning] = useState(false)
  const [result, setResult]               = useState(null)
  const [runMs, setRunMs]                 = useState(null)
  const [debugData, setDebugData]         = useState(null)   // { result, trace, trace_truncated }
  const [debugRunMs, setDebugRunMs]       = useState(null)
  const [activeTab, setActiveTab]         = useState('output')

  const fileRef = useRef()

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const validatePayload = useCallback((text) => {
    try {
      JSON.parse(text)
      setPayloadError('')
      return true
    } catch (e) {
      setPayloadError(e.message)
      return false
    }
  }, [])

  const handlePayloadChange = (e) => {
    setPayloadText(e.target.value)
    if (payloadError) validatePayload(e.target.value)
  }

  const handleFormat = () => {
    const formatted = tryFormat(payloadText)
    setPayloadText(formatted)
    validatePayload(formatted)
  }

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target.result
      setPayloadText(text)
      validatePayload(text)
    }
    reader.readAsText(file)
  }

  const handleRun = async () => {
    if (!validatePayload(payloadText)) return
    const payload = JSON.parse(payloadText)

    setIsRunning(true)
    setResult(null)
    setDebugData(null)
    const t0 = performance.now()

    try {
      const { data } = await runConnector(filename, payload, configValues, code)
      setResult(data)
      setActiveTab('output')
    } catch (err) {
      setResult({ status: 'error', message: err.response?.data?.detail || err.message })
      setActiveTab('output')
    } finally {
      setRunMs(Math.round(performance.now() - t0))
      setIsRunning(false)
    }
  }

  const handleRunDebug = async () => {
    if (!validatePayload(payloadText)) return
    const payload = JSON.parse(payloadText)

    setIsDebugRunning(true)
    setResult(null)
    setDebugData(null)
    const t0 = performance.now()

    try {
      const { data } = await runConnectorDebug(filename, payload, configValues, code)
      setDebugData(data)
      setActiveTab('debug')
    } catch (err) {
      setDebugData({
        result: { status: 'error', message: err.response?.data?.detail || err.message },
        trace: [],
        trace_truncated: false,
      })
      setActiveTab('debug')
    } finally {
      setDebugRunMs(Math.round(performance.now() - t0))
      setIsDebugRunning(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-12 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-2xl bg-[#252526] border border-[#3c3c3c] rounded-xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#3c3c3c] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-[#0078d4] flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">Run connector</p>
              <p className="text-[11px] text-[#858585] font-mono leading-tight">{filename}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#858585] hover:text-white hover:bg-[#ffffff15] w-7 h-7 flex items-center justify-center rounded transition text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Config params section */}
          {detectedParams.length > 0 ? (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
                </svg>
                <span className="text-[10px] font-semibold text-[#858585] uppercase tracking-widest">
                  Configuration
                </span>
                <span className="text-[10px] text-[#444] font-normal">
                  — detected from <code className="text-[#6cb6f5]">__init__</code>
                </span>
              </div>
              <div className="space-y-2.5">
                {detectedParams.map((key) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="w-40 text-xs font-mono text-[#cccccc] truncate flex-shrink-0" title={key}>
                      {key}
                    </label>
                    <input
                      type={isSensitiveKey(key) ? 'password' : 'text'}
                      value={configValues[key] ?? ''}
                      onChange={(e) =>
                        setConfigValues((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      placeholder={`Enter ${key}…`}
                      className="flex-1 bg-[#1e1e1e] border border-[#3c3c3c] rounded-md px-3 py-1.5 text-xs font-mono text-[#cccccc] placeholder-[#444] outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4] transition"
                    />
                  </div>
                ))}
              </div>
            </section>
          ) : (
            <div className="flex items-center gap-2 py-2 px-3 bg-[#1e1e1e] border border-[#3c3c3c] rounded-md text-xs text-[#555]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              No <code className="text-[#6cb6f5] mx-0.5">cfg.get(...)</code> calls detected — no config params needed.
            </div>
          )}

          {/* Payload section */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                <span className="text-[10px] font-semibold text-[#858585] uppercase tracking-widest">
                  Payload
                </span>
                <span className="text-[10px] text-[#444]">— passed as <code className="text-[#6cb6f5]">process(payload)</code></span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleFormat}
                  className="text-[11px] text-[#858585] hover:text-[#cccccc] px-2 py-0.5 border border-[#3c3c3c] hover:border-[#505050] rounded transition"
                >
                  Format
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1 text-[11px] text-[#858585] hover:text-[#cccccc] px-2 py-0.5 border border-[#3c3c3c] hover:border-[#505050] rounded transition"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Upload .json
                </button>
                <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileUpload} />
              </div>
            </div>

            <textarea
              value={payloadText}
              onChange={handlePayloadChange}
              rows={9}
              spellCheck={false}
              className={`w-full bg-[#1a1a1a] border rounded-lg px-4 py-3 text-[12px] font-mono text-[#cccccc] resize-y outline-none transition leading-relaxed ${
                payloadError
                  ? 'border-[#f44747] focus:ring-1 focus:ring-[#f44747]'
                  : 'border-[#3c3c3c] focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]'
              }`}
            />
            {payloadError && (
              <p className="mt-1.5 text-[11px] text-[#f44747] flex items-center gap-1.5">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {payloadError}
              </p>
            )}
          </section>

          {/* Result / Debug tabs */}
          {(result || debugData) && (
            <section>
              {/* Tab bar — only show when debugData exists (otherwise just output) */}
              {debugData ? (
                <div className="flex gap-0 border-b border-[#3c3c3c] mb-4">
                  <button
                    onClick={() => setActiveTab('output')}
                    className={`px-4 py-1.5 text-xs font-medium border-b-2 -mb-px transition ${
                      activeTab === 'output'
                        ? 'border-[#0078d4] text-[#cccccc]'
                        : 'border-transparent text-[#555] hover:text-[#858585]'
                    }`}
                  >
                    Output
                  </button>
                  <button
                    onClick={() => setActiveTab('debug')}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium border-b-2 -mb-px transition ${
                      activeTab === 'debug'
                        ? 'border-[#f44747] text-[#f44747]'
                        : 'border-transparent text-[#555] hover:text-[#858585]'
                    }`}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 2l1.5 1.5"/><path d="M14.5 3.5L16 2"/><path d="M9 7.5a3 3 0 0 0 6 0"/><path d="M6.5 9a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2"/><path d="M17.5 9a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2"/><path d="M4.5 14.5L3 16"/><path d="M19.5 14.5L21 16"/><path d="M9 21a3 3 0 0 0 6 0v-5a3 3 0 0 0-6 0v5z"/><path d="M9 12h6"/>
                    </svg>
                    Debug
                  </button>
                </div>
              ) : null}

              {activeTab === 'output' && (result || debugData?.result) && (
                <ResultViewer
                  result={debugData ? debugData.result : result}
                  runMs={debugData ? debugRunMs : runMs}
                />
              )}
              {activeTab === 'debug' && debugData && (
                <DebugViewer
                  trace={debugData.trace}
                  truncated={debugData.trace_truncated}
                  runMs={debugRunMs}
                  filename={filename}
                />
              )}
            </section>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#3c3c3c] flex-shrink-0">
          <p className="text-[11px] text-[#444] flex items-center gap-1.5">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Runs in a server-side sandbox · 30s timeout
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-sm text-[#858585] hover:text-[#cccccc] px-4 py-2 rounded-md hover:bg-[#ffffff10] transition"
            >
              Close
            </button>
            <button
              onClick={handleRunDebug}
              disabled={isDebugRunning || isRunning || !!payloadError}
              className="flex items-center gap-2 bg-[#3a1a1a] hover:bg-[#4a2020] border border-[#f4474740] hover:border-[#f4474780] disabled:opacity-40 disabled:cursor-not-allowed text-[#f44747] text-sm font-medium px-4 py-2 rounded-md transition"
            >
              {isDebugRunning ? (
                <>
                  <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Debugging…
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2l1.5 1.5"/><path d="M14.5 3.5L16 2"/><path d="M9 7.5a3 3 0 0 0 6 0"/><path d="M6.5 9a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2"/><path d="M17.5 9a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2"/><path d="M4.5 14.5L3 16"/><path d="M19.5 14.5L21 16"/><path d="M9 21a3 3 0 0 0 6 0v-5a3 3 0 0 0-6 0v5z"/><path d="M9 12h6"/>
                  </svg>
                  Run with Debug
                </>
              )}
            </button>
            <button
              onClick={handleRun}
              disabled={isRunning || isDebugRunning || !!payloadError}
              className="flex items-center gap-2 bg-[#0078d4] hover:bg-[#1a8ad4] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-md transition"
            >
              {isRunning ? (
                <>
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Running…
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  Run
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
